import React, { useState, useEffect, useRef } from 'react';
import { getCachedSymbolNamesSync, getCachedSymbolNames, normalizeSymbolNameEntry } from '../utils/symbolNamesCache';
import { Check, X } from 'lucide-react';

const SymbolAutocomplete = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Type symbol symbol...",
  className = "",
  disabled = false,
  showValidation = true,
  maxSuggestions = 10,
  clearOnSelect = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isValid, setIsValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [companyName, setCompanyName] = useState(null);
  const [symbolMap, setSymbolMap] = useState({});
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load symbol names on mount
  useEffect(() => {
    const loadSymbolNames = async () => {
      // Try sync first
      let symbols = getCachedSymbolNamesSync();
      
      // If no cache, fetch async
      if (!symbols || Object.keys(symbols).length === 0) {
        symbols = await getCachedSymbolNames();
      }
      
      if (symbols && Object.keys(symbols).length > 0) {
        setSymbolMap(symbols);
      }
    };
    
    loadSymbolNames();
  }, []);

  // Filter suggestions based on input (search by both symbol and company name)
  useEffect(() => {
    if (!value || value.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsValid(null);
      setCompanyName(null);
      return;
    }

    const searchTerm = value.toUpperCase().trim();
    const searchTermLower = value.toLowerCase().trim();
    const searchTermOriginal = value.trim();
    
    const exactKey = Object.keys(symbolMap).find(k => k.toUpperCase() === searchTerm.toUpperCase());
    const exactMeta = exactKey ? normalizeSymbolNameEntry(exactKey, symbolMap[exactKey]) : null;
    if (exactMeta && exactMeta.company_name && exactKey) {
      setIsValid(true);
      setCompanyName(exactMeta.company_name);
    } else {
      const exactCompanyMatch = Object.entries(symbolMap).find(([sid, raw]) => {
        const m = normalizeSymbolNameEntry(sid, raw);
        const c = m.company_name;
        return c && (
          c.toUpperCase() === searchTerm ||
          c.toLowerCase() === searchTermLower ||
          c === searchTermOriginal
        );
      });
      
      if (exactCompanyMatch) {
        const m = normalizeSymbolNameEntry(exactCompanyMatch[0], exactCompanyMatch[1]);
        setIsValid(true);
        setCompanyName(m.company_name);
      } else if (showValidation && searchTerm.length >= 1) {
        // Only mark as invalid if user has typed at least 1 character and no matches found
        setIsValid(false);
        setCompanyName(null);
      }
    }

    // Generate suggestions - search by both symbol symbol and company name
    if (symbolMap && Object.keys(symbolMap).length > 0) {
      const filtered = Object.entries(symbolMap)
        .map(([symbolId, raw]) => ({
          symbol: symbolId,
          meta: normalizeSymbolNameEntry(symbolId, raw)
        }))
        .filter(({ symbol, meta }) => {
          const idUpper = symbol.toUpperCase();
          const tickUpper = (meta.symbol || '').toUpperCase();
          const companyUpper = (meta.company_name || '').toUpperCase();
          const companyLower = (meta.company_name || '').toLowerCase();
          const exchUpper = (meta.exchange || '').toUpperCase();
          const symbolMatch =
            idUpper.startsWith(searchTerm) ||
            idUpper.includes(searchTerm) ||
            tickUpper.startsWith(searchTerm) ||
            tickUpper.includes(searchTerm);
          const exchangeMatch = exchUpper.includes(searchTerm) || exchUpper.startsWith(searchTerm);
          const companyMatch =
            companyUpper.startsWith(searchTerm) ||
            companyUpper.includes(searchTerm) ||
            companyLower.includes(searchTermLower);
          return symbolMatch || companyMatch || exchangeMatch;
        })
        .sort((a, b) => {
          const symbolAUpper = a.symbol.toUpperCase();
          const symbolBUpper = b.symbol.toUpperCase();
          const companyAUpper = (a.meta.company_name || '').toUpperCase();
          const companyBUpper = (b.meta.company_name || '').toUpperCase();
          if (symbolAUpper === searchTerm) return -1;
          if (symbolBUpper === searchTerm) return 1;
          if (symbolAUpper.startsWith(searchTerm) && !symbolBUpper.startsWith(searchTerm)) return -1;
          if (symbolBUpper.startsWith(searchTerm) && !symbolAUpper.startsWith(searchTerm)) return 1;
          if (companyAUpper.startsWith(searchTerm) && !companyBUpper.startsWith(searchTerm)) return -1;
          if (companyBUpper.startsWith(searchTerm) && !companyAUpper.startsWith(searchTerm)) return 1;
          if (symbolAUpper.includes(searchTerm) && !symbolBUpper.includes(searchTerm)) return -1;
          if (symbolBUpper.includes(searchTerm) && !symbolAUpper.includes(searchTerm)) return 1;
          return a.symbol.localeCompare(b.symbol);
        })
        .slice(0, maxSuggestions);
      
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && searchTerm.length > 0);
      
      // If we have suggestions and one matches exactly by symbol, mark as valid
      if (filtered.length > 0 && filtered[0].symbol.toUpperCase() === searchTerm) {
        setIsValid(true);
        setCompanyName(filtered[0].meta.company_name);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value, symbolMap, maxSuggestions, showValidation]);

  const handleInputChange = (e) => {
    // Allow mixed case for company name searches, but convert symbol symbols to uppercase
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);
  };

  const handleSelect = (symbol, company) => {
    // clearOnSelect: parent owns value via onSelect (avoids filling input + overlay,
    // and avoids onChange('') clearing selection before onSelect runs)
    if (clearOnSelect) {
      setCompanyName(null);
      setIsValid(null);
    } else {
      onChange(symbol);
      setIsValid(true);
      setCompanyName(company);
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (onSelect) {
      onSelect(symbol, company);
    }
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const selected = suggestions[selectedIndex];
          handleSelect(selected.symbol, selected.meta.company_name);
        } else if (suggestions.length === 1) {
          handleSelect(suggestions[0].symbol, suggestions[0].meta.company_name);
        } else if (isValid) {
          // If valid symbol, allow form submission
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleBlur = (e) => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show suggestions if we have any, or if user has typed something
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    } else if (value.trim().length > 0 && symbolMap && Object.keys(symbolMap).length > 0) {
      // If user has typed something but suggestions haven't updated yet, trigger update
      // This is handled by the useEffect, but we can show suggestions if they exist
      setShowSuggestions(true);
    }
  };

  const handleBlurWithFocus = (e) => {
    setIsFocused(false);
    handleBlur(e);
  };

  return (
    <div className="symbol-autocomplete-container">
      <div className="symbol-autocomplete-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlurWithFocus}
          placeholder={placeholder}
          className={`${className} symbol-autocomplete-input ${showValidation && value ? 'has-validation' : ''} ${companyName && isValid && value.trim() && !isFocused ? 'overlay-active' : ''}`}
          disabled={disabled}
          autoComplete="off"
        />
        {/* Display symbol + company name overlay when valid symbol is selected and not focused */}
        {companyName && isValid && value.trim() && !isFocused && (
          <div className={`symbol-autocomplete-overlay ${showValidation && value ? 'has-validation' : ''}`}>
            {(() => {
              const v = value.trim();
              const keyMatch = Object.keys(symbolMap).find(k => k.toUpperCase() === v.toUpperCase());
              const sid =
                keyMatch ||
                Object.entries(symbolMap).find(
                  ([sid, raw]) => normalizeSymbolNameEntry(sid, raw).company_name === companyName
                )?.[0] ||
                v;
              const m = normalizeSymbolNameEntry(sid, symbolMap[sid]);
              return (
                <>
                  <span className="symbol-autocomplete-symbol">{m.symbol || sid}</span>
                  {m.exchange ? (
                    <span className="symbol-autocomplete-overlay-exch"> · {m.exchange}</span>
                  ) : null}
                  <span className="symbol-autocomplete-separator"> — </span>
                  <span className="symbol-autocomplete-company">{m.company_name || companyName}</span>
                </>
              );
            })()}
          </div>
        )}
        {showValidation && value && (
          <div className="symbol-autocomplete-validation">
            {isValid === true && (
              <Check size={18} color="green" />
            )}
            {isValid === false && (
              <X size={18} color="red" />
            )}
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="symbol-autocomplete-suggestions"
        >
          {suggestions.map((item, index) => (
            <div
              key={item.symbol}
              onClick={() => handleSelect(item.symbol, item.meta.company_name)}
              className={`symbol-autocomplete-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="symbol-autocomplete-suggestion-meta">
                <div className="symbol-autocomplete-suggestion-top">
                  <span className="symbol-autocomplete-suggestion-symbol">{item.meta.symbol || '—'}</span>
                  {item.meta.exchange ? (
                    <span className="symbol-autocomplete-suggestion-exchange">{item.meta.exchange}</span>
                  ) : null}
                </div>
                <div className="symbol-autocomplete-suggestion-company">
                  {item.meta.company_name}
                </div>
              </div>
              {item.symbol.toUpperCase() === value.toUpperCase().trim() && (
                <Check size={16} color="green" />
              )}
            </div>
          ))}
        </div>
      )}

      {showValidation && isValid === false && value.trim().length >= 1 && (
        <div className="symbol-autocomplete-error">
          Select available symbol from below
        </div>
      )}
    </div>
  );
};

export default SymbolAutocomplete;

